import { AuthProvider as OidcAuthProvider, useAuth } from "react-oidc-context";
import { useEffect } from "react";
import { oidcConfig } from "../config/authConfig";
import { signinState } from "./returnTo";

function onSigninCallback() {
  // Remove OIDC query/hash parameters from URL after successful sign-in.
  // Only clean the URL if there are search params (state, code, etc.)
  if (window.location.search || window.location.hash) {
    window.history.replaceState({}, document.title, window.location.pathname);
  }
}

function onRemoveUser() {
  // Clear any leftover OIDC artefacts from localStorage on sign-out
  window.location.replace("/");
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  return (
    <OidcAuthProvider
      {...oidcConfig}
      onSigninCallback={onSigninCallback}
      onRemoveUser={onRemoveUser}
    >
      <SilentRenewErrorHandler />
      {children}
    </OidcAuthProvider>
  );
}

/**
 * Listens for silent-renew failures (e.g. expired refresh token) and
 * forces a full re-login so the user doesn't stay stuck with a stale session.
 */
function SilentRenewErrorHandler() {
  const auth = useAuth();

  useEffect(() => {
    return auth.events.addAccessTokenExpired(() => {
      console.warn("[Auth] access token expired, redirecting to login");
      // Preserve the current deep link so OidcCallback returns the user here.
      auth.signinRedirect({
        state: signinState(window.location.pathname + window.location.search),
      });
    });
  }, [auth]);

  useEffect(() => {
    return auth.events.addSilentRenewError((error) => {
      console.error("[Auth] silent renew failed:", error);
      // Refresh token is likely expired — force a new login, resuming the
      // user's current location after the round-trip.
      auth.signinRedirect({
        state: signinState(window.location.pathname + window.location.search),
      });
    });
  }, [auth]);

  return null;
}
